const ATTACHMENT_FIELD_TYPES = new Set(['file', 'signaturepad']);
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const HTML_TAG_PATTERN = /<!--[\s\S]*?-->|<\/?[a-z][^>]*>/gi;
const CONTAINS_HTML_PATTERN = /<!--|<\/?[a-z][^>]*>/i;
const LINE_BREAK_TAGS = /<br\s*\/?>/gi;
const LIST_ITEM_OPEN = /<li\b[^>]*>/gi;
const BLOCK_END_TAGS = /<\/(p|div|li|h[1-6]|tr|blockquote|ul|ol)\s*>/gi;
const HTML_ENTITIES = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
};
/**
 * Flatten rich-text answers to plain text. Workflows generate their reply as
 * JSON, and echoing back markup with quotes and newlines corrupts it.
 */
export function htmlToPlainText(value) {
    if (!CONTAINS_HTML_PATTERN.test(value))
        return value;
    return value
        .replace(LINE_BREAK_TAGS, '\n')
        .replace(LIST_ITEM_OPEN, '- ')
        .replace(BLOCK_END_TAGS, '\n')
        .replace(HTML_TAG_PATTERN, '')
        .replace(/&[a-z]+;|&#\d+;/gi, (entity) => HTML_ENTITIES[entity.toLowerCase()] ?? entity)
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** Answer values keep their keys and structure; only markup is removed. */
function stripHtmlFromValues(value) {
    if (typeof value === 'string')
        return htmlToPlainText(value);
    if (Array.isArray(value))
        return value.map(stripHtmlFromValues);
    if (isPlainObject(value)) {
        const result = {};
        for (const [key, child] of Object.entries(value)) {
            result[key] = stripHtmlFromValues(child);
        }
        return result;
    }
    return value;
}
function stripHtmlFromData(data) {
    const stripped = stripHtmlFromValues(data);
    return isPlainObject(stripped) ? stripped : {};
}
/**
 * Remove SurveyJS HTML display elements and HTML properties before sending a
 * form definition to a workflow. Other strings keep their text content but
 * lose markup such as <strong> or <br>.
 */
export function stripSurveyHtml(value) {
    if (typeof value === 'string') {
        return value.replace(HTML_TAG_PATTERN, '');
    }
    if (Array.isArray(value)) {
        return value
            .filter((item) => !(isPlainObject(item) && item.type === 'html'))
            .map(stripSurveyHtml);
    }
    if (isPlainObject(value)) {
        const result = {};
        for (const [key, child] of Object.entries(value)) {
            if (key.toLowerCase() === 'html')
                continue;
            result[key] = stripSurveyHtml(child);
        }
        return result;
    }
    return value;
}
/** SurveyJS file questions store { name, type, content } with base64 data URLs. */
export function isSurveyFileAttachmentValue(value) {
    if (typeof value === 'string') {
        return value.startsWith('data:') && value.length > 256;
    }
    if (!Array.isArray(value) || value.length === 0) {
        return false;
    }
    return value.every((item) => isPlainObject(item) &&
        typeof item.name === 'string' &&
        typeof item.content === 'string' &&
        (item.content.startsWith('data:') || typeof item.type === 'string'));
}
export function collectAttachmentFieldPaths(definition) {
    const paths = new Set();
    function walk(elements, panelPrefix) {
        for (const el of elements || []) {
            if (el.type === 'panel') {
                walk(el.elements, el.name || '');
            }
            else if (el.type === 'paneldynamic') {
                if (el.name)
                    paths.add(el.name);
                for (const templateEl of el.templateElements || []) {
                    if (templateEl.name && ATTACHMENT_FIELD_TYPES.has(templateEl.type || '')) {
                        paths.add(`${el.name}[0].${templateEl.name}`);
                        paths.add(templateEl.name);
                    }
                }
            }
            else if (el.name && ATTACHMENT_FIELD_TYPES.has(el.type || '')) {
                const path = panelPrefix ? `${panelPrefix}.${el.name}` : el.name;
                paths.add(path);
                paths.add(el.name);
            }
        }
    }
    for (const page of definition?.pages || []) {
        walk(page.elements, '');
    }
    if (definition?.elements) {
        walk(definition.elements, '');
    }
    return paths;
}
function pathMatchesAttachmentField(path, attachmentPaths) {
    if (attachmentPaths.has(path))
        return true;
    const fieldName = path.split('.').pop()?.replace(/\[\d+\]$/, '') || path;
    if (attachmentPaths.has(fieldName))
        return true;
    return [...attachmentPaths].some((attachmentPath) => path === attachmentPath ||
        path.endsWith(`.${attachmentPath}`) ||
        attachmentPath.endsWith(`.${fieldName}`));
}
function stripAttachmentValues(value, attachmentPaths, currentPath = '') {
    if (currentPath && pathMatchesAttachmentField(currentPath, attachmentPaths)) {
        return undefined;
    }
    if (isSurveyFileAttachmentValue(value)) {
        return undefined;
    }
    if (Array.isArray(value)) {
        const nextPath = currentPath ? `${currentPath}[]` : '';
        const items = value
            .map((item, index) => stripAttachmentValues(item, attachmentPaths, currentPath ? `${currentPath}[${index}]` : `[${index}]`))
            .filter((item) => item !== undefined);
        if (nextPath && pathMatchesAttachmentField(nextPath, attachmentPaths)) {
            return undefined;
        }
        return items;
    }
    if (isPlainObject(value)) {
        const result = {};
        for (const [key, child] of Object.entries(value)) {
            const path = currentPath ? `${currentPath}.${key}` : key;
            if (pathMatchesAttachmentField(path, attachmentPaths))
                continue;
            if (isSurveyFileAttachmentValue(child))
                continue;
            const stripped = stripAttachmentValues(child, attachmentPaths, path);
            if (stripped !== undefined) {
                result[key] = stripped;
            }
        }
        return result;
    }
    return value;
}
export function stripAttachmentData(data, attachmentPaths) {
    const stripped = stripAttachmentValues(data, attachmentPaths);
    return isPlainObject(stripped) ? stripped : {};
}
export function sanitizeWorkflowPayload(request) {
    const sanitizedDefinition = stripSurveyHtml(request.formDefinition ?? {});
    const definition = (isPlainObject(sanitizedDefinition) ? sanitizedDefinition : {});
    const attachmentPaths = collectAttachmentFieldPaths(definition);
    return {
        ...request,
        formDefinition: definition,
        currentData: stripHtmlFromData(stripAttachmentData(request.currentData ?? {}, attachmentPaths)),
        conversationHistory: (request.conversationHistory ?? []).map((turn) => ({
            ...turn,
            surveyData: stripHtmlFromData(stripAttachmentData(turn.surveyData ?? {}, attachmentPaths)),
        })),
    };
}
